<script lang="ts">
    import { operationHashBeginning } from "../../../store"
    import hash from "object-hash"
    import { createEventDispatcher } from "svelte"
    import MiniLoader from "../../widgets/MiniLoader.svelte"
    import AttachementField from "../../widgets/fields/AttachementField.svelte"
    import { postMail, updateOperationOut, getAttachement, getActiveOfferTemplate } from "../../../api"
    import { newNotification, login } from "../../../store"
    import Richtext from "../../widgets/Richtext.svelte"
    import { emitEvent } from "../../../eventBus"
    import { replacePlaceholder } from "../../../../../api/hooks/config-client"
    import { _ } from "svelte-i18n"

    export let operation: OperationOut
    export let type: string

    let template: Partial<DocumentTemplate> = {}
    const templateType = type === "sendOffer" ? "offer" : "orderConfirmation"

    const handleTemplateError = () => {
        newNotification({
            class: "error",
            html: $_("components.operationsEmail.errorNotification"),
            timeout: 10000,
            removeOnNewLocation: true,
        })
    }

    const fetchActiveOfferTemplate = (dealerNumber: number, type: string | undefined, source: any = null) => {
        return getActiveOfferTemplate(dealerNumber, type, source)
            .then((response) => {
                if (response.data) {
                    template = response.data[0]
                }
                if (response.data.length == 0) {
                    getActiveOfferTemplate(dealerNumber, type, null).then((response) => {
                        if (response.data) {
                            template = response.data[0]
                        }
                    })
                }
            })
            .catch(handleTemplateError)
    }

    // Main Execution
    fetchActiveOfferTemplate(operation.dealerNumber, templateType, operation?.source).catch(() =>
        fetchActiveOfferTemplate(operation.dealerNumber, templateType)
    )

    let files: Attachement[] = null
    let attachmentsProm: {
        [opNum: number]: Promise<{ count?: number; data: Attachement[] }>
    } = {}

    async function loadAttachments(op: OperationOut) {
        if (!attachmentsProm[op.number]) {
            attachmentsProm[op.number] = getAttachement(op.id)
        }
        try {
            const r = await attachmentsProm[op.number]
            return r.data
        } catch (e) {}
        return []
    }

    async function getAttachments(operation: OperationOut) {
        if (operation) {
            delete attachmentsProm[operation.number]
            files = await loadAttachments(operation)
        }
    }
    $: getAttachments(operation)
    const dispatch = createEventDispatcher()
    let pending: boolean = false
    let form: {
        email: string
        cc: string
        bcc: string
        subject: string
        message: string
    }
    let formValid = form && form.email !== "" && form.subject !== "" && form.message !== ""
    const resetForm = (texts: DocumentTemplateTexts) => {
        form = {
            email: operation.endCustomerEmail || "",
            cc: "",
            bcc: "",
            subject:
                replacePlaceholder(texts?.emailSubject, operation) ||
                $_("components.operationsEmail.sendOfferSubject", { values: { number: operation.number } }),
            message: texts?.emailBody
                ? replacePlaceholder(texts?.emailBody, operation)
                : type == "sendOffer"
                  ? $_("components.operationsEmail.sendOfferMessage", {
                        values: {
                            firstName: $login?.account?.firstname || "",
                            lastName: $login?.account?.lastname || "",
                            email: $login?.account?.email || "",
                        },
                    })
                  : $_("components.operationsEmail.sendOrderConfirmationMessage", {
                        values: {
                            firstName: $login?.account?.firstname || "",
                            lastName: $login?.account?.lastname || "",
                            email: $login?.account?.email || "",
                        },
                    }),
        }
        formValid = form && form.email !== "" && form.subject !== "" && form.message !== ""
    }

    const saveOp = async (operation: OperationOut) => {
        operation?.additionalPositions?.forEach((p) => (p.netPrice = Number(p.netPrice)))
        operation?.positions?.forEach((p) => {
            p.netPrice = Number(p.netPrice)
            p?.additionalItems?.forEach((s) => {
                s.netPrice = Number(s.netPrice)
            })
        })
        await updateOperationOut(operation)
            .then(() => {
                $operationHashBeginning = hash(operation)
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: `${$_("components.operationsEmail.offerCouldNotBeSent")}: ` + e.message,
                })
            })
            .finally(() => {
                dispatch("closeTab")
            })
    }
    let dontReset = false
    let loading = false
    const submit = () => {
        dontReset = true
        pending = true
        loading = true
        let finalForm: any = { ...form }
        finalForm.cc = finalForm.cc.split(/[,;]/).map((s: string) => s.trim())
        finalForm.bcc = finalForm.bcc.split(/[,;]/).map((s: string) => s.trim())
        finalForm.operation = operation

        postMail(type == "sendOffer" ? "sendOffer" : "sendOrderConfirmation", finalForm)
            .then(() => {
                if (!operation.statusSummary) operation.statusSummary = {}
                if (type == "sendOffer") operation.statusSummary.emailed = 100
                else operation.statusSummary.orderConfimationEmailed = 100
                operation.emailSentTimestamp = new Date()
                operation.emailSentTo = finalForm.email

                // update happens within the postMail, as put could be disallowed if sent before
                loading = false
                emitEvent("operationUpdated", operation)
                newNotification({
                    class: "success",
                    html:
                        type == "sendOffer"
                            ? $_("components.operationsEmail.offerSentNotification")
                            : $_("components.operationsEmail.orderConfirmationSentNotification"),
                })
                dispatch("closeTab")
            })
            .catch((e) => {
                newNotification({
                    class: "error",
                    html: $_("components.operationsEmail.errorNotification"),
                    timeout: 10000,
                    removeOnNewLocation: true,
                })
            })
            .finally(() => {
                pending = false
            })
    }

    const emailRegex = /^[\w.-]+@[\w.-]+\.\w+$/

    function isEmailListValid(value: string): boolean {
        if (!value || value.trim() === "") return true
        return value
            .split(/[,;]/)
            .map((s) => s.trim())
            .filter(Boolean)
            .every((addr) => emailRegex.test(addr))
    }

    $: ccInvalid = form && form.cc && form.cc.trim() !== "" && !isEmailListValid(form.cc)
    $: bccInvalid = form && form.bcc && form.bcc.trim() !== "" && !isEmailListValid(form.bcc)

    $: formValid =
        form &&
        form.email !== "" &&
        form?.email?.match(emailRegex) &&
        form.subject !== "" &&
        form.message !== "" &&
        !ccInvalid &&
        !bccInvalid

    $: if (operation && template && !dontReset) resetForm(operation.documentTemplateTextContent || template.texts)
</script>

<style>
    input.invalid {
        border-color: #e74c3c !important;
        background-color: #fff5f5;
    }
    input.invalid:focus {
        box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.25);
    }
</style>

<div class="operation-email">
    <h3>
        {operation.type == 1
            ? $_("components.operationsEmail.sendOfferSubject", { values: { number: operation.number } })
            : $_("components.operationsEmail.sendOrderConfirmationSubject", { values: { number: operation.number } })}
    </h3>
    {#if loading}
        <div style="padding-top: 50px; padding-left: 50px;">
            <MiniLoader />
        </div>
    {/if}
    <form on:submit|preventDefault="{submit}" class="send-offer-form">
        <div class="row">
            <div class="col-md-12">
                <div class="inputholder haslabel">
                    <label for="email">{$_("components.operationsEmail.recipient")}</label>
                    <input type="email" id="email" placeholder="" bind:value="{form.email}" required="{true}" />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-6">
                <div class="inputholder haslabel">
                    <label for="cc">{$_("components.operationsEmail.cc")}</label>
                    <input
                        type="text"
                        id="cc"
                        placeholder="beispiel1@gmail.com; beispiel2@gmail.com"
                        bind:value="{form.cc}"
                        class:invalid="{ccInvalid}"
                    />
                </div>
            </div>
            <div class="col-md-6">
                <div class="inputholder haslabel">
                    <label for="bcc">{$_("components.operationsEmail.bcc")}</label>
                    <input
                        type="text"
                        id="bcc"
                        placeholder="beispeil1@gmail.com; beispiel2@gmail.com"
                        bind:value="{form.bcc}"
                        class:invalid="{bccInvalid}"
                    />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="inputholder haslabel">
                    <label for="subject">{$_("components.operationsEmail.subject")}</label>
                    <input type="text" id="subject" placeholder="" bind:value="{form.subject}" />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="inputholder haslabel" style="margin-bottom: 20px;">
                    <span>{$_("components.operationsEmail.message")}</span>
                    <Richtext placeholder="" bind:value="{form.message}" />
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="attachment">
                    <div class="attachment-title">{$_("components.operationsEmail.attachment")}</div>
                    <div class="attachment-items">
                        {#if files == null}
                            <MiniLoader />
                        {:else if files?.length}
                            <div class="attachements">
                                {#each files.filter((f) => !f.name.match(/^\d{7,8}_\d{3}\-/)) as file}
                                    {#if file.name.includes(type == "sendOffer" ? "-A" : type == "sendOrderConfirmation" ? "-O" : "thisisaninvalidkey") || (!file.name.match(/\.xml$/) && file.name.includes(type == "offer" ? "-A" : "thisisaninvalidkey"))}
                                        <AttachementField
                                            viaId="{true}"
                                            {file}
                                            {operation}
                                            className="overlay_auftrag"
                                        />
                                    {/if}
                                {/each}
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </form>
</div>

<div class="overlay_buttons">
    <div class="layout layout-gap-sm align-items-center justify-content-space-between" style="width: 100%;">
        <div></div>
        <button class="btn btn-secondary btn-blue" on:click="{submit}" disabled="{!formValid || pending}">
            {$_("components.operationsEmail.sendButton")}
        </button>
    </div>
</div>
